import React from 'react';
import { Phone, Mail, MapPin, Instagram, Heart, ArrowUp, Star } from 'lucide-react';
import { laverieInfo } from '../data/mock';
import BasqueFlag from './BasqueFlag';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-black text-white relative overflow-hidden">
      {/* Basque stripe */}
      <div className="h-1.5 bg-gradient-to-r from-[#9B1B30] via-white to-[#006633]" />
      
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#9B1B30]/5 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-16 md:py-20 relative">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-[#9B1B30] to-[#7A1526] rounded-xl flex items-center justify-center shadow-lg">
                <span className="font-black text-2xl">LR</span>
              </div>
              <div>
                <h3 className="text-xl font-bold">Lavoir des Remparts</h3>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-[#9B1B30] font-bold tracking-wider">LAVERIE 3.0</p>
                  <BasqueFlag size={18} />
                </div>
              </div>
            </div>
            <p className="text-gray-400 mb-4 max-w-md leading-relaxed">
              Votre laverie moderne au cœur de Bayonne. 
              Lessive & Adoucissant OFFERTS, machines professionnelles, 
              tarifs pros jusqu'à -30%.
            </p>
            <div className="flex items-center gap-2 text-[#9B1B30] font-semibold mb-6">
              <Star size={16} />
              <span>Qualité • Service • Proximité</span>
            </div>
            <div className="flex gap-4 items-center">
              <a
                href={laverieInfo.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center hover:bg-[#9B1B30] transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={22} />
              </a>
              <BasqueFlag size={50} />
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-bold text-lg mb-6 text-white">Navigation</h4>
            <ul className="space-y-3">
              {[
                { label: 'Services', href: '#services' },
                { label: 'Galerie', href: '#galerie' },
                { label: 'Tarifs', href: '#tarifs' },
                { label: 'Votre secteur', href: '#marches' },
                { label: 'Contact', href: '#contact' }
              ].map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-[#9B1B30] transition-colors font-medium"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-lg mb-6 text-white">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={laverieInfo.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-gray-400 hover:text-[#9B1B30] transition-colors"
                >
                  <MapPin size={18} className="flex-shrink-0 mt-1" />
                  <span>{laverieInfo.address}<br />{laverieInfo.city}</span>
                </a>
              </li>
              <li>
                <a
                  href={`tel:${laverieInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-3 text-gray-400 hover:text-[#9B1B30] transition-colors"
                >
                  <Phone size={18} />
                  <span>{laverieInfo.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${laverieInfo.email}`}
                  className="flex items-center gap-3 text-gray-400 hover:text-[#9B1B30] transition-colors"
                >
                  <Mail size={18} />
                  <span>{laverieInfo.email}</span>
                </a>
              </li>
            </ul>

            {/* Hours */}
            <div className="mt-6 bg-white/5 rounded-xl p-4 border border-white/10">
              <p className="text-sm text-gray-500 mb-1">Horaires</p>
              <p className="font-bold text-white">{laverieInfo.hours.days}</p>
              <p className="font-bold text-[#9B1B30]">{laverieInfo.hours.open} - {laverieInfo.hours.close}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
            <p>© {currentYear} Lavoir des Remparts. Tous droits réservés.</p>
            <div className="flex items-center gap-4">
              <p className="flex items-center gap-2">
                Fait avec <Heart size={14} className="text-[#9B1B30]" /> à Bayonne
                <BasqueFlag size={18} />
              </p>
              <button
                onClick={scrollToTop}
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#9B1B30] transition-colors"
                aria-label="Retour en haut"
              >
                <ArrowUp size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
